import React, { useEffect, useState } from "react";
import { Send, Copy, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

export default function EmailDraftDialog({ open, onOpenChange, contact, propertyId, dealId, onLogged }) {
  const [draft, setDraft] = useState({ subject: "", body: "", to: "", to_name: "" });
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !contact) return;
    setCopied(false);
    api.post("/communications/email-draft", { contact_id: contact.id, property_id: propertyId, deal_id: dealId })
      .then(({ data }) => setDraft({ ...data, to: data.to || contact.email || "", to_name: data.to_name || contact.name || "" }))
      .catch((e) => toast.error(formatApiError(e)));
  }, [open, contact, propertyId, dealId]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`A: ${draft.to}\nOggetto: ${draft.subject}\n\n${draft.body}`);
      setCopied(true); toast.success("Testo copiato negli appunti");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Impossibile copiare"); }
  };

  const openMailto = () => {
    const url = `mailto:${encodeURIComponent(draft.to)}?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`;
    window.open(url, "_blank");
  };

  const downloadPdf = async () => {
    if (!dealId) return;
    try {
      const res = await api.get(`/deals/${dealId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `deal_${dealId}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF non disponibile per questa opportunità"); }
  };

  const logSent = async () => {
    setBusy(true);
    try {
      await api.post("/communications", {
        contact_id: contact.id,
        property_id: propertyId,
        deal_id: dealId,
        medium: "email", direction: "out", status: "sent",
        subject: draft.subject, body: draft.body,
        recipient_email: draft.to, recipient_name: draft.to_name,
      });
      toast.success("Invio registrato nel CRM");
      onOpenChange(false);
      onLogged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="email-draft-dialog">
        <DialogHeader>
          <DialogTitle>Bozza email · {contact?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Destinatario</Label><Input value={draft.to_name} onChange={(e) => setDraft({ ...draft, to_name: e.target.value })} data-testid="email-to-name" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} data-testid="email-to" /></div>
          </div>
          <div><Label className="text-xs">Oggetto</Label><Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} data-testid="email-subject" /></div>
          <div><Label className="text-xs">Corpo</Label><Textarea rows={10} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="email-body" /></div>
          {dealId && (
            <button onClick={downloadPdf} className="text-xs text-primary hover:underline" data-testid="email-pdf-link">
              📎 Scarica PDF allegato — deal_{dealId}.pdf
            </button>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={copy} className="gap-2" data-testid="email-copy">
            {copied ? <ClipboardCheck className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            Copia
          </Button>
          <Button variant="outline" onClick={openMailto} data-testid="email-mailto">Apri nel client email</Button>
          <Button onClick={logSent} disabled={busy || !draft.to} className="gap-2" data-testid="email-log-sent">
            <Send className="w-4 h-4" /> {busy ? "Salvo…" : "Registra invio"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
